/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  useEffect,
  useState,
} from 'react';

import { Helmet } from 'react-helmet';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import LoadingContent from 'app/modules/loading/LoadingContent';

import {
  fetchContracts,
} from 'app/services/contracts';

import {
  formatLongDateToDate,
} from 'app/services/dates';

import {
  fetchBranchLessons,
  getLessonBranch,
  getLessonPath,
} from 'app/services/lessons';

import PurchaseMenu from 'app/utils/PurchaseMenu'
import LessonItem from 'app/pages/Lessons/LessonItem'

type Modality = 'class' | 'reservation';
type ContractType = 'single' | 'recurrence';

type ContractListProps = {
  modality: Modality,
  type: ContractType,
  basePath: string,
  title?: string,
}

const MODALITY_LABEL: Record<Modality, { singular: string, plural: string }> = {
  class: { singular: 'aula', plural: 'aulas' },
  reservation: { singular: 'locação', plural: 'locações' },
}

const ContractList = ({ modality, type, basePath, title = 'Aulas mensal' }: ContractListProps) => {
  const { contracts, auth } = useSelector((state : any) => ({
    contracts: state.contracts,
    auth: state.auth,
  }), shallowEqual);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [list, setList] = useState<any>([]);
  const [activeList, setActiveList] = useState<any>([]);
  const [lessons, setLessons] = useState<any>([]);

  useEffect(() => {
    dispatch<any>(fetchContracts(modality, type)).then((response: any) => {
      const active = response.filter((value: any) => value.attributes.is_active);

      setActiveList(active);
      setList(response.filter((value: any) => !value.attributes.is_active));

      if (modality === 'class') {
        getNextLessons(active);
      }
    });
  }, [modality, type]);

  // next lessons the client participates in, from the branches of the active class contracts
  const getNextLessons = (activeContracts: any) => {
    const branches = new Map();

    activeContracts.forEach((contract: any) => {
      const branch = getBranch(contract);

      if (branch?.id) {
        branches.set(branch.id, branch);
      }
    });

    Promise.all(Array.from(branches.keys()).map((branchId: string) =>
      fetchBranchLessons(branchId, { onlyMine: true }).catch(() => [])
    )).then((results: any) => {
      setLessons(results.filter((branchLessons: any) => branchLessons.length > 0));
    });
  }

  useEffect(() => {
    if (!auth.user) {
      navigate('/')
    }
  }, [auth]);

  const getBranch = (contract: any) => {
    return contract?.relationships?.schedule_spot?.[0]?.relationships?.schedule?.relationships?.field?.relationships?.branch;
  }

  const showItem = (contract: any) => {
    return <div className='col-lg-6 mb-1 mb-lg-0' key={`contract-${contract.id}`}>
      <div className='d-flex justify-content-center'>
        <div
          className='card card-dashed flex-row flex-stack p-4 mb-6 w-100 cursor-pointer'
          onClick={() => navigate(`${basePath}/${contract.id}`)}
        >
          <div className='d-flex align-items-center me-2'>
            <div className='flex-grow-1'>
              <div className='d-flex align-items-center fs-2 fw-bold flex-wrap pb-2'>
                {contract?.relationships?.service?.attributes?.name}
              </div>
              <div className='fs-7 fw-semibold text-gray-700 pb-1'>
                {contract?.relationships?.service?.attributes?.recurrence_times} {MODALITY_LABEL[modality].singular} por semana
              </div>
              <div className='fs-7 fw-semibold text-gray-700 pb-1'>
                Início: {formatLongDateToDate(contract.attributes.start_at)}
              </div>
              {contract.attributes.finish_at && <div className='fs-7 fw-semibold text-gray-700 pb-1'>
                Término: {formatLongDateToDate(contract.attributes.finish_at)}
              </div>}
            </div>
          </div>
          <div className='d-flex ms-5'>
            <div className='flex-grow-1 text-uppercase'>
              {contract.attributes.is_active && <span className='flex d-flex text-primary fs-6 fw-bold text-center'>
                Ativo
              </span>}

              {!contract.attributes.is_active && <span className='flex d-flex text-gray-700 fs-6 fw-bold text-center'>
                Finalizado
              </span>}

              {getBranch(contract)?.attributes?.name && <span className='flex d-flex fs-7 opacity-50 justify-content-end uppercase mt-2'>
                {getBranch(contract).attributes.name}
              </span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  return (<>
    <Helmet>
      <title>Minhas assinaturas - {title} - Atacante App</title>
    </Helmet>

    <PurchaseMenu recurrence />

    <div className='card mb-5 mb-xl-10'>
      <div className='card-body pt-9 pb-0 mb-13'>
        {lessons.length > 0 && <div className='mb-10'>
          <div className='mb-10'>
            <h1 className='mb-3'>
              Próximas aulas
            </h1>
            <div className='text-muted fw-semibold fs-6'>
              Não vai conseguir ir? Marque falta e ganhe um crédito de reposição.
            </div>
          </div>
          {lessons.map((branchLessons: any) => {
            const branch = getLessonBranch(branchLessons[0]);

            return <div className='mt-10' key={`lessons-branch-${branch?.id}`}>
              {lessons.length > 1 && <h3 className='mb-5'>
                {branch?.attributes?.name}
              </h3>}

              <div className='row'>
                {branchLessons.map((lesson: any) => <div className='col-lg-6' key={`lesson-${lesson.id}-${lesson.attributes.start_at}`}>
                  <LessonItem
                    lesson={lesson}
                    showDate
                    onClick={() => navigate(getLessonPath(lesson.id, lesson.attributes.date))}
                  />
                </div>)}
              </div>
            </div>
          })}
        </div>}

        {activeList.length > 0 && <div className='mb-10'>
          <div className='mb-10'>
            <h1 className='mb-3'>
              Assinaturas ativas
            </h1>
          </div>
          <div className='row mt-10'>
            {activeList.map((contract: any) => showItem(contract))}
          </div>
        </div>}

        {list.length > 0 && <>
          <div className='mb-10'>
            <h1 className='mb-3'>
              Histórico de assinaturas
            </h1>
          </div>
          <div className='row mt-10'>
            {list.map((contract: any) => showItem(contract))}
          </div>
        </>}

        {contracts.listLoading === false && list.length === 0 && activeList.length === 0 && <div className='my-10 text-center'>
          <h2 className='mb-3'>
            Nenhuma assinatura foi realizada
          </h2>
          <div className='text-muted fw-semibold fs-5'>
            Encontre unidades próximas a você
          </div>
          <div className='text-muted fw-semibold fs-5'>
            para realizar um agendamento.
          </div>
        </div>}

        {contracts.listLoading === true && list.length === 0 && activeList.length === 0 && <LoadingContent />}
      </div>
    </div>
  </>)
}

export {
  ContractList,
}
